import {
	type AudioTaskType,
	audioBufferAtom,
	audioErrorAtom,
	audioTaskStateAtom,
	auditionTimeAtom,
	currentTimeAtom,
} from "$/modules/audio/states/index.ts";
import { AudioWorkerClient } from "$/modules/audio/workers/audio-worker-client";
import { globalStore } from "$/states/store.ts";
import { log } from "$/utils/logging";
import { audioBufferToWav } from "./audio-buffer-to-wav";

let auditionRafId: number | null = null;

export class AudioEngine extends EventTarget {
	public workerClient: AudioWorkerClient;

	//#region Audio context basics
	private _ctx: AudioContext | null = null;
	get ctx() {
		if (this._ctx) return this._ctx;
		this._ctx = new AudioContext({
			latencyHint: "interactive",
		});
		log(
			"AudioContext created with latency",
			this._ctx.baseLatency,
			this._ctx.outputLatency,
		);
		return this._ctx;
	}

	private gainNode: GainNode | null = null;
	private get gain() {
		if (this.gainNode) return this.gainNode;
		this.gainNode = this.ctx.createGain();
		this.gainNode.gain.value = 0.5;
		this.gainNode.connect(this.ctx.destination);
		return this.gainNode;
	}
	//#endregion

	constructor() {
		super();
		this.workerClient = new AudioWorkerClient({
			onTaskStart: (type: AudioTaskType) => {
				globalStore.set(audioTaskStateAtom, { type, progress: 0 });
			},
			onTaskProgress: (progress: number) => {
				const current = globalStore.get(audioTaskStateAtom);
				if (current) {
					globalStore.set(audioTaskStateAtom, { ...current, progress });
				}
			},
			onTaskEnd: () => {
				globalStore.set(audioTaskStateAtom, null);
			},
			onError: (errorMessage: string) => {
				console.error("[AudioEngine] Worker Error:", errorMessage);
				globalStore.set(audioTaskStateAtom, null);
				globalStore.set(audioErrorAtom, errorMessage);
			},
		});
	}

	//#region Audio element
	// 通常再生には、波形・試聴と同じデコード済み PCM の WAV を渡す。
	private _audioEl: HTMLAudioElement | null = null;
	private mediaSourceNode: MediaElementAudioSourceNode | null = null;
	private audioObjectUrl: string | null = null;
	get audioEl() {
		if (this._audioEl) return this._audioEl;
		this._audioEl = document.createElement("audio");
		this._audioEl.preload = "auto";
		this.setupAudioListeners();
		return this._audioEl;
	}

	/** Connect AudioElement to AudioContext, called after load finished */
	private connectAudioToContext() {
		if (this.mediaSourceNode) return;
		this.mediaSourceNode = this.ctx.createMediaElementSource(this.audioEl);
		this.mediaSourceNode.connect(this.gain);
	}

	/** Handle browser autoplay policy */
	private async resumeContext() {
		if (this.ctx.state === "suspended") {
			await this.ctx.resume();
			log("AudioContext resumed");
		}
	}

	/** Link audio element events into engine events */
	private setupAudioListeners() {
		const audioEl = this._audioEl;
		if (!audioEl) return;
		audioEl.addEventListener("seeked", () => {
			if (!audioEl.seeking) this.pendingSeekTime = null;
			this.dispatchEvent(new Event("music-seeked"));
		});
		const events = {
			play: "music-resume",
			pause: "music-pause",
			timeupdate: "music-seeked",
			ended: "music-pause",
			volumechange: "volume-change",
			ratechange: "music-playback-rate-change",
		};
		Object.entries(events).forEach(([event, engineEvent]) => {
			audioEl.addEventListener(event, () => {
				this.dispatchEvent(new Event(engineEvent));
			});
		});
	}
	//#endregion

	//#region Playback
	private auditionSourceNode: AudioBufferSourceNode | null = null;
	private pendingSeekTime: number | null = null;
	private playbackRequestId = 0;

	get musicLoaded() {
		return !!this.musicBuffer;
	}

	get musicPlaying() {
		return (
			!!this.musicBuffer &&
			!!this._audioEl &&
			!this._audioEl.paused &&
			!this._audioEl.ended
		);
	}

	get musicCurrentTime() {
		return this.pendingSeekTime ?? this._audioEl?.currentTime ?? 0;
	}

	get musicDuration() {
		return this.musicBuffer?.duration ?? 0;
	}

	private _musicPlayBackRate = 1;
	get musicPlayBackRate() {
		return this._musicPlayBackRate;
	}
	set musicPlayBackRate(v: number) {
		if (this._audioEl) {
			this._audioEl.playbackRate = v;
		}
		this._musicPlayBackRate = v;
		this.dispatchEvent(new Event("music-playback-rate-change"));
	}

	get volume() {
		return this.gain.gain.value;
	}
	set volume(v: number) {
		this.gain.gain.value = v;
		this.dispatchEvent(new Event("volume-change"));
	}

	get ctxCurrentTime() {
		return this.ctx.currentTime;
	}
	get ctxBaseLatency() {
		return this.ctx.baseLatency;
	}
	get ctxOutputLatency() {
		return this.ctx.outputLatency;
	}

	seekMusic(offset: number) {
		if (!this.musicBuffer || !this._audioEl || !Number.isFinite(offset)) return;
		const target = Math.max(0, Math.min(offset, this.musicDuration));
		// 連続する相対シークも打刻も、完了待ちの間は最新の要求位置を参照する。
		// 表示用 atom だけを補正しても、次の ←→ が古い位置を使う問題は残る。
		this.pendingSeekTime = target;
		this._audioEl.currentTime = target;
		globalStore.set(currentTimeAtom, Math.floor(target * 1000));
		this.dispatchEvent(new Event("music-seeked"));
	}

	async resumeOrSeekMusic(offset?: number) {
		if (!this.musicBuffer || !this._audioEl) return;
		const requestId = ++this.playbackRequestId;
		if (offset !== undefined) this.seekMusic(offset);
		try {
			await this.resumeContext();
			if (requestId !== this.playbackRequestId) return;
			// resume() の待機中に行われたシークを古い offset で上書きしない。
			await this._audioEl.play();
		} catch (error) {
			if (requestId !== this.playbackRequestId) return;
			globalStore.set(audioErrorAtom, String(error));
		}
	}

	pauseMusic() {
		++this.playbackRequestId;
		if (!this._audioEl) return;
		this._audioEl.pause();
	}

	private stopAudition() {
		if (this.auditionSourceNode) {
			try {
				this.auditionSourceNode.stop(0);
				this.auditionSourceNode.disconnect();
			} catch (e) {
				console.error("停止 AudioNode 失败:", e);
			}
			this.auditionSourceNode = null;
		}

		if (auditionRafId) {
			cancelAnimationFrame(auditionRafId);
			auditionRafId = null;
		}

		globalStore.set(auditionTimeAtom, null);
	}

	auditionRange(startTimeInSeconds: number, endTimeInSeconds: number) {
		if (!this.musicBuffer) return;
		this.stopAudition();

		const durationInSeconds = endTimeInSeconds - startTimeInSeconds;

		if (durationInSeconds <= 0) {
			return;
		}

		this.resumeContext();

		const audioCtxStartTime = this.ctx.currentTime;
		const mediaStartTime = startTimeInSeconds;

		const source = this.ctx.createBufferSource();
		source.buffer = this.musicBuffer;
		source.connect(this.gain);
		this.auditionSourceNode = source;

		const progressLoop = () => {
			const elapsedTime = this.ctx.currentTime - audioCtxStartTime;
			const currentAuditionTime = mediaStartTime + elapsedTime;

			if (currentAuditionTime >= endTimeInSeconds) {
				globalStore.set(auditionTimeAtom, null);
				auditionRafId = null;
			} else {
				globalStore.set(auditionTimeAtom, currentAuditionTime);
				auditionRafId = requestAnimationFrame(progressLoop);
			}
		};

		source.addEventListener("ended", () => {
			if (this.auditionSourceNode === source) {
				if (auditionRafId) {
					cancelAnimationFrame(auditionRafId);
					auditionRafId = null;
				}
				globalStore.set(auditionTimeAtom, null);
				this.auditionSourceNode = null;
			}
			source.disconnect();
		});

		source.start(audioCtxStartTime, mediaStartTime, durationInSeconds);
		auditionRafId = requestAnimationFrame(progressLoop);
	}

	//#endregion

	//#region Load sound
	private musicBuffer: AudioBuffer | null = null;

	private loadController: AbortController | null = null;

	private clearAudioSource() {
		this.pendingSeekTime = null;
		this.audioEl.removeAttribute("src");
		this.audioEl.load();
		if (this.audioObjectUrl) {
			URL.revokeObjectURL(this.audioObjectUrl);
			this.audioObjectUrl = null;
		}
	}

	async loadMusic(src: Blob): Promise<HTMLAudioElement> {
		this.loadController?.abort();
		const controller = new AbortController();
		this.loadController = controller;
		const { signal } = controller;
		const checkAborted = () => {
			if (signal.aborted) {
				throw new DOMException("Audio load superseded", "AbortError");
			}
		};
		this.pauseMusic();
		this.stopAudition();
		this.musicBuffer = null;
		globalStore.set(audioBufferAtom, null);
		globalStore.set(audioErrorAtom, null);
		this.clearAudioSource();
		this.dispatchEvent(new Event("music-unload"));
		this.dispatchEvent(new Event("music-loading"));

		try {
			let buffer: AudioBuffer;
			try {
				buffer = await this.ctx.decodeAudioData(await src.arrayBuffer());
			} catch (error) {
				checkAborted();
				log("[AudioEngine] Trying transcoding fallback:", error);
				const wav = await this.workerClient.transcodeToWav(src);
				checkAborted();
				buffer = await this.ctx.decodeAudioData(await wav.arrayBuffer());
			}
			checkAborted();

			// 圧縮ファイルのシーク索引を使わず、波形と完全に同じサンプルを再生する。
			const url = URL.createObjectURL(audioBufferToWav(buffer));
			this.audioObjectUrl = url;
			const audioEl = this.audioEl;
			await new Promise<void>((resolve, reject) => {
				const cleanup = () => {
					audioEl.removeEventListener("loadedmetadata", onLoaded);
					audioEl.removeEventListener("error", onError);
					signal.removeEventListener("abort", onAbort);
				};
				const onLoaded = () => {
					cleanup();
					resolve();
				};
				const onError = () => {
					cleanup();
					reject(new Error(audioEl.error?.message || "PCM audio load failed"));
				};
				const onAbort = () => {
					cleanup();
					reject(new DOMException("Audio load superseded", "AbortError"));
				};
				audioEl.addEventListener("loadedmetadata", onLoaded);
				audioEl.addEventListener("error", onError);
				signal.addEventListener("abort", onAbort, { once: true });
				audioEl.src = url;
				audioEl.load();
			});
			checkAborted();
			audioEl.playbackRate = this._musicPlayBackRate;
			this.connectAudioToContext();
			this.musicBuffer = buffer;
			globalStore.set(audioBufferAtom, buffer);
			this.dispatchEvent(new Event("music-load"));
			return audioEl;
		} catch (error) {
			if (!signal.aborted) {
				this.clearAudioSource();
				this.dispatchEvent(new Event("music-load-error"));
			}
			throw error;
		}
	}

	playSound(
		audioBuffer: AudioBuffer,
		when?: number,
		offset?: number,
		duration?: number,
	) {
		if (!this.ctx) return;
		const source = this.ctx.createBufferSource();
		source.buffer = audioBuffer;
		source.connect(this.gain);
		source.start(when, offset, duration);
		source.addEventListener("ended", () => {
			source.disconnect();
		});
	}

	playNode(node: AudioScheduledSourceNode, when?: number, stop?: number) {
		node.connect(this.gain);
		node.start(when);
		node.addEventListener("ended", () => {
			node.disconnect();
		});
		if (stop) node.stop(stop);
	}
	//#endregion

	//#region Misc
	decodeAudioData(
		audioData: ArrayBuffer,
		successCallback?: DecodeSuccessCallback | null,
		errorCallback?: DecodeErrorCallback | null,
	): Promise<AudioBuffer> {
		return this.ctx.decodeAudioData(audioData, successCallback, errorCallback);
	}
	//#endregion
}

export const audioEngine = new AudioEngine();
